import { Link } from "react-router-dom";
import { logger } from "../logger";
import type { ModificationResult } from "../model";
import {
  useProjects,
  useActionItemsWithoutProject,
  useCreateProject,
  useUpdateProject,
  useDeleteProject,
  useCreateActionItem,
  useUpdateActionItem,
  useDeleteActionItem,
} from "../hooks/useData";
import { ProjectManager, VoiceCaptureSystem } from "../components_v2";
import type { ProjectType, ActionItemType } from "../components_v2";

export default function Home() {
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const { data: globalActionItems = [], isLoading: globalActionItemsLoading } =
    useActionItemsWithoutProject();
  const createProject = useCreateProject();
  const updateProject = useUpdateProject();
  const deleteProject = useDeleteProject();
  const createActionItem = useCreateActionItem();
  const updateActionItem = useUpdateActionItem();
  const deleteActionItem = useDeleteActionItem();

  const handleModificationsApproved = async (
    modifications: ModificationResult,
  ) => {
    try {
      // Apply project modifications
      for (const projectMod of modifications.projectModifications) {
        if (projectMod.operation === "create") {
          await createProject.mutateAsync({
            name: projectMod.name || "New Project",
            desiredOutcome: projectMod.desiredOutcome || "",
          });
        } else if (projectMod.operation === "update" && projectMod.id) {
          await updateProject.mutateAsync({
            id: projectMod.id,
            name: projectMod.name,
            desiredOutcome: projectMod.desiredOutcome,
          });
        } else if (projectMod.operation === "delete" && projectMod.id) {
          await deleteProject.mutateAsync(projectMod.id);
        }
      }

      // Apply action item modifications
      for (const actionMod of modifications.actionItemModifications) {
        if (actionMod.operation === "create") {
          await createActionItem.mutateAsync({
            content: actionMod.content || "New Action Item",
            projectId: actionMod.projectId,
            status: "actionable",
          });
        } else if (actionMod.operation === "update" && actionMod.id) {
          await updateActionItem.mutateAsync({
            id: actionMod.id,
            content: actionMod.content,
          });
        } else if (actionMod.operation === "delete" && actionMod.id) {
          await deleteActionItem.mutateAsync({
            id: actionMod.id,
            projectId: actionMod.projectId,
          });
        }
      }
    } catch (error) {
      logger.error("Failed to apply modifications", { error });
    }
  };

  // Convert data to v2 format
  const v2Projects: ProjectType[] = projects.map((p) => ({
    id: p.id,
    name: p.name,
    desiredOutcome: p.desiredOutcome,
    actionItems:
      p.actionItems?.map((item) => ({
        id: item.id,
        content: item.content,
        status: item.status || "actionable",
        date: item.date,
        delegatedTo: item.delegatedTo,
      })) || [],
  }));

  const v2GlobalActionItems: ActionItemType[] = globalActionItems.map(
    (item) => ({
      id: item.id,
      content: item.content,
      status: item.status || "actionable",
      date: item.date,
      delegatedTo: item.delegatedTo,
    }),
  );

  // No need for update handlers - React Query manages state automatically

  if (projectsLoading || globalActionItemsLoading) {
    return (
      <main className="font-sans m-8">
        <div>Loading...</div>
      </main>
    );
  }

  return (
    <main className="font-sans m-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Project Capture & Management</h1>
        <Link
          to="/action-items"
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
        >
          View All Action Items
        </Link>
      </div>

      {/* V2 Project Manager Component */}
      <ProjectManager
        projects={v2Projects}
        globalActionItems={v2GlobalActionItems}
      />

      {/* V2 Voice Capture System */}
      <VoiceCaptureSystem
        onModificationsApproved={handleModificationsApproved}
      />
    </main>
  );
}
